import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Save, GraduationCap } from "lucide-react";

const TEXTAREAS = [
  ["overview", "Overview"],
  ["who_its_for", "Who It's For"],
  ["how_it_works", "How It Works"],
  ["training", "Training"],
  ["requirements_overview", "Requirements / Readiness"],
  ["benefits", "Opportunity & Benefits"],
  ["timeline", "Typical Timeline"],
  ["what_happens_next", "What Happens Next"],
];
const ICON_OPTS = ["Store", "Handshake", "UserPlus", "GraduationCap", "Briefcase"];
const MODE_OPTS = [
  ["all_states", "All States"],
  ["not_state_specific", "Not State-Specific"],
  ["selected_states", "Selected States Only"],
  ["all_except", "All Except Listed"],
];
const AUDIENCES = ["candidate", "internal"];

const FieldLabel = ({ children }) => <label className="text-xs font-semibold text-slate-500 uppercase">{children}</label>;

export default function ProgramPagesEditor({ canEdit }) {
  const [pages, setPages] = useState([]);
  const [slug, setSlug] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/programs/pages").then((r) => {
      const items = r.data.items || [];
      setPages(items);
      setSlug((cur) => cur || (items[0] && items[0].slug));
    }).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!slug) return;
    const pg = pages.find((p) => p.slug === slug);
    if (pg) setForm(JSON.parse(JSON.stringify(pg)));
  }, [slug, pages]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["applicable_states", "excluded_states"].forEach((k) => {
        if (typeof payload[k] === "string") payload[k] = payload[k].split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
      });
      await api.put(`/programs/pages/${slug}`, payload);
      toast.success("Program page saved");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!form) return <p className="text-sm text-slate-400 mt-8">Loading…</p>;

  const states = (k) => Array.isArray(form[k]) ? form[k].join(", ") : (form[k] || "");

  return (
    <div className="mt-4 grid lg:grid-cols-[220px_1fr] gap-6" data-testid="program-pages-editor">
      {/* page list */}
      <div className="space-y-1.5">
        {pages.map((p) => (
          <button key={p.slug} onClick={() => setSlug(p.slug)} data-testid={`pp-select-${p.slug}`}
            className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm transition-colors flex items-center gap-2 ${slug === p.slug ? "border-[#006090] bg-[#006090]/5 font-semibold text-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
            <GraduationCap className="w-4 h-4 text-[#0D9488] shrink-0" />
            <span className="truncate">{p.title || p.slug}</span>
            {p.active === false && <span className="ml-auto text-[10px] text-amber-600 border border-amber-200 rounded px-1">off</span>}
          </button>
        ))}
      </div>

      {/* form */}
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={form.active !== false} onCheckedChange={(v) => set("active", v)} disabled={!canEdit} data-testid="pp-active" /> Active</label>
            <div className="flex items-center gap-2">
              <FieldLabel>Order</FieldLabel>
              <Input type="number" value={form.display_order ?? 0} onChange={(e) => set("display_order", parseInt(e.target.value || "0", 10))} className="w-20 h-8" disabled={!canEdit} data-testid="pp-order" />
            </div>
          </div>
          {canEdit && <Button onClick={save} disabled={saving} data-testid="pp-save" className="bg-[#006090] hover:bg-[#004E75]"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save Page"}</Button>}
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div><FieldLabel>Title</FieldLabel><Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} disabled={!canEdit} data-testid="pp-title" /></div>
          <div><FieldLabel>Icon</FieldLabel>
            <Select value={form.icon || "GraduationCap"} onValueChange={(v) => set("icon", v)} disabled={!canEdit}>
              <SelectTrigger data-testid="pp-icon"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{ICON_OPTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><FieldLabel>Effective Date</FieldLabel><Input type="date" value={(form.effective_date || "").slice(0, 10)} onChange={(e) => set("effective_date", e.target.value || null)} disabled={!canEdit} data-testid="pp-effective" /></div>
        </div>

        <div><FieldLabel>Hero Image URL</FieldLabel><Input value={form.hero_image || ""} onChange={(e) => set("hero_image", e.target.value)} placeholder="https://…" disabled={!canEdit} data-testid="pp-hero" /></div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><FieldLabel>Badge Label</FieldLabel><Input value={form.badge_label || ""} onChange={(e) => set("badge_label", e.target.value)} disabled={!canEdit} data-testid="pp-badge" /></div>
          <div><FieldLabel>Hero Headline</FieldLabel><Input value={form.hero_headline || ""} onChange={(e) => set("hero_headline", e.target.value)} disabled={!canEdit} data-testid="pp-hero-headline" /></div>
        </div>
        <div><FieldLabel>Hero Subheadline</FieldLabel><Textarea value={form.hero_subheadline || ""} onChange={(e) => set("hero_subheadline", e.target.value)} rows={2} disabled={!canEdit} data-testid="pp-hero-subheadline" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><FieldLabel>Primary CTA Label</FieldLabel><Input value={form.cta_primary_label || ""} onChange={(e) => set("cta_primary_label", e.target.value)} disabled={!canEdit} data-testid="pp-cta-primary" /></div>
          <div><FieldLabel>Secondary CTA Label</FieldLabel><Input value={form.cta_secondary_label || ""} onChange={(e) => set("cta_secondary_label", e.target.value)} disabled={!canEdit} data-testid="pp-cta-secondary" /></div>
        </div>
        <div><FieldLabel>Capital Requirement Note</FieldLabel><Textarea value={form.capital_note || ""} onChange={(e) => set("capital_note", e.target.value)} rows={2} disabled={!canEdit} data-testid="pp-capital-note" placeholder="e.g. Capital to invest must be available (not required to spend)…" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><FieldLabel>Capital Link Label</FieldLabel><Input value={form.capital_link_label || ""} onChange={(e) => set("capital_link_label", e.target.value)} disabled={!canEdit} data-testid="pp-capital-link-label" placeholder="Learn more about capital requirements" /></div>
          <div><FieldLabel>Capital Link Target (slug)</FieldLabel><Input value={form.capital_link_slug || ""} onChange={(e) => set("capital_link_slug", e.target.value)} disabled={!canEdit} data-testid="pp-capital-link-slug" placeholder="capital-requirements" /></div>
        </div>
        <div><FieldLabel>Page Disclaimer (shown at bottom)</FieldLabel><Textarea value={form.disclaimer || ""} onChange={(e) => set("disclaimer", e.target.value)} rows={2} disabled={!canEdit} data-testid="pp-disclaimer" placeholder="Leave blank to use the default disclaimer." /></div>
        <label className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={form.listed !== false} onCheckedChange={(v) => set("listed", v)} disabled={!canEdit} data-testid="pp-listed" /> Listed in Programs menu &amp; portal grid</label>
        <div><FieldLabel>Content Text (PDF converted to text)</FieldLabel><Textarea value={form.content_text || ""} onChange={(e) => set("content_text", e.target.value)} rows={6} disabled={!canEdit} data-testid="pp-content-text" /></div>
        <ListEditor label="Related Information Links" items={form.related_links || []} onChange={(v) => set("related_links", v)} canEdit={canEdit} testid="related" fields={[["label", "Label"], ["slug", "Target slug"]]} />

        {TEXTAREAS.map(([k, lbl]) => (
          <div key={k}><FieldLabel>{lbl}</FieldLabel>
            <Textarea value={form[k] || ""} onChange={(e) => set(k, e.target.value)} rows={k === "overview" || k === "how_it_works" ? 3 : 2} disabled={!canEdit} data-testid={`pp-${k}`} />
          </div>
        ))}

        <ListEditor label="Key Steps" items={form.key_steps || []} onChange={(v) => set("key_steps", v)} canEdit={canEdit} testid="key-steps" fields={[["", "Step"]]} simple />
        <ListEditor label="FAQs" items={form.faqs || []} onChange={(v) => set("faqs", v)} canEdit={canEdit} testid="faqs" fields={[["q", "Question"], ["a", "Answer"]]} />
        <ListEditor label="Resources" items={form.resources || []} onChange={(v) => set("resources", v)} canEdit={canEdit} testid="resources" fields={[["label", "Label"], ["url", "URL"]]} />
        <ListEditor label="At a Glance (stat cards)" items={form.at_a_glance || []} onChange={(v) => set("at_a_glance", v)} canEdit={canEdit} testid="glance" fields={[["label", "Label"], ["value", "Value"]]} />
        <ListEditor label="Why Choose (cards)" items={form.why_choose || []} onChange={(v) => set("why_choose", v)} canEdit={canEdit} testid="why" fields={[["title", "Title"], ["text", "Text"], ["icon", "Icon (e.g. Building2)"]]} />
        <ListEditor label="How It Works — Journey steps" items={form.journey || []} onChange={(v) => set("journey", v)} canEdit={canEdit} testid="journey" fields={[["label", "Step"], ["description", "Description"]]} />
        <ListEditor label="Incentives & Opportunities" items={form.incentives || []} onChange={(v) => set("incentives", v)} canEdit={canEdit} testid="incentives" fields={[["title", "Title"], ["text", "Text"], ["qualifier", "Qualifier (fine print)"]]} />
        <ListEditor label="Comparison (is this right for you?)" items={form.comparison || []} onChange={(v) => set("comparison", v)} canEdit={canEdit} testid="comparison" fields={[["program", "Program"], ["text", "Best for…"]]} />
        <ListEditor label="Preparing — What You'll Need (high-level)" items={form.what_youll_need || []} onChange={(v) => set("what_youll_need", v)} canEdit={canEdit} testid="need" fields={[["", "Item"]]} simple />

        {/* Applicability & Audience */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><FieldLabel>State Applicability</FieldLabel>
              <Select value={form.applicability_mode || "all_states"} onValueChange={(v) => set("applicability_mode", v)} disabled={!canEdit}>
                <SelectTrigger data-testid="pp-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{MODE_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.applicability_mode === "selected_states" && (
              <div><FieldLabel>Applicable States (comma codes)</FieldLabel><Input value={states("applicable_states")} onChange={(e) => set("applicable_states", e.target.value)} placeholder="CA, TX" disabled={!canEdit} data-testid="pp-applicable-states" /></div>
            )}
            {form.applicability_mode === "all_except" && (
              <div><FieldLabel>Excluded States (comma codes)</FieldLabel><Input value={states("excluded_states")} onChange={(e) => set("excluded_states", e.target.value)} placeholder="NY" disabled={!canEdit} data-testid="pp-excluded-states" /></div>
            )}
          </div>
          <div>
            <FieldLabel>Audience</FieldLabel>
            <div className="flex items-center gap-4 mt-1.5">
              {AUDIENCES.map((a) => {
                const on = (form.audience || []).includes(a);
                return (
                  <label key={a} className="flex items-center gap-2 text-sm text-slate-700 capitalize">
                    <Switch checked={on} disabled={!canEdit} data-testid={`pp-audience-${a}`}
                      onCheckedChange={(v) => set("audience", v ? [...new Set([...(form.audience || []), a])] : (form.audience || []).filter((x) => x !== a))} />
                    {a}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        {!canEdit && <p className="text-xs text-amber-600">You have view-only access. Account or Group Admins can edit these pages.</p>}
      </div>
    </div>
  );
}

function ListEditor({ label, items, onChange, canEdit, testid, fields, simple }) {
  const add = () => onChange([...(items || []), simple ? "" : Object.fromEntries(fields.map(([k]) => [k, ""]))]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const upd = (i, k, v) => onChange(items.map((it, idx) => idx === i ? (simple ? v : { ...it, [k]: v }) : it));
  return (
    <div data-testid={`pp-list-${testid}`}>
      <div className="flex items-center justify-between">
        <FieldLabel>{label}</FieldLabel>
        {canEdit && <Button variant="ghost" size="sm" onClick={add} className="text-xs text-[#006090]" data-testid={`pp-add-${testid}`}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>}
      </div>
      <div className="space-y-2 mt-1">
        {(items || []).length === 0 && <p className="text-xs text-slate-400">None</p>}
        {(items || []).map((it, i) => (
          <div key={i} className="flex items-start gap-2" data-testid={`pp-${testid}-row-${i}`}>
            <div className="flex-1 space-y-1.5">
              {fields.map(([k, ph]) => (
                k === "a" ? (
                  <Textarea key={k} value={simple ? it : (it[k] || "")} onChange={(e) => upd(i, k, e.target.value)} placeholder={ph} rows={2} disabled={!canEdit} />
                ) : (
                  <Input key={k} value={simple ? it : (it[k] || "")} onChange={(e) => upd(i, k, e.target.value)} placeholder={ph} disabled={!canEdit} />
                )
              ))}
            </div>
            {canEdit && <button onClick={() => remove(i)} className="text-slate-400 hover:text-rose-500 mt-2" data-testid={`pp-${testid}-remove-${i}`}><Trash2 className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}
